/**
 * The capture flow's phase machine, as a pure reducer.
 *
 *   reduce(state, event, now) → { state, effects }
 *   select(state, now)        → view model for the screen
 *
 * Phases: off · unsupported · scanning · fitting · stale · ready · recording ·
 * journal · processing · kept · sending · scored · lost (reason 'lost' | 'stopped').
 *
 * Rules the flow keeps (Canvas 05 §03):
 *   2. every reading carries its age; a reading older than STALE_AFTER_MS
 *      cannot confirm readiness;
 *   3. one next step at a time, from a fixed vocabulary;
 *   4. advice changes at most every GUIDANCE_MIN_MS;
 *   6. during the recording nothing moves — guidance is frozen and the only
 *      thing that changes is the timer; the only sound is the end cue
 *      (owner ruling D2, 2026-10-02): a `cue` effect at full length or when
 *      the headband is lost, never on Stop early.
 *
 * The paired session (Sprint 1.9.13, rulings R1–R5). `plan` is `'ec'` (today's
 * recording, the default) or `'eo_ec'`: eyes open for RECORD_SECONDS as block 1,
 * then eyes closed for RECORD_SECONDS as block 2, two linked recordings sharing a
 * `pairId`. The `recording` phase runs once per block. At the end of block 1 one
 * reduce emits, in this order, `cue switch` · `record stop` (seal block 1) ·
 * `record start` (block 2): no intermediate phase, no pause, no button. Block 1
 * is packaged and sent while block 2 records (`blockOne` tracks it); the journal
 * belongs to the pair and travels with block 2 only. Lost in block 1 is today's
 * lost with one more way out, `record_ec_only`; lost in block 2 keeps block 1 as
 * a single eyes-open recording. Nothing dispatches `set_plan` while
 * `PAIRED_SESSION_ENABLED` is off, so the machine then behaves exactly as before.
 *
 * Effects are data, not calls. In `calibrations/` they were HTTP requests to a
 * local server; here they address the device layer (`lib/headband`) and the
 * in-page session builder (`lib/capture/session`). The runner in the React
 * screen executes them in order and dispatches `then` / `fail` with the result.
 * That is what lets a test assert which operation a transition triggers.
 *
 * Ported from `calibrations/static/capture/phase.mjs` (Sprint 1.9.7).
 */

import type { CueKind } from './cue'
import type { Activity, CaptureRole, ExperimentPlan, ExperimentRun } from '../experiments/api'
import { badgeFor, copyFor, guide, steps as stepsFor, type Guidance, type LostReason, type Phase } from './guidance'
import { layout, legendText, rows as mapRows, type Layout } from './headmap'
import { GUIDANCE_MIN_MS, HOLD_MS, RECORD_SECONDS, STALE_AFTER_MS, heldCount, okSet, tierOf, type Tier } from './quality'

export const LOST_SAMPLES_MS = 3000 // no signal frame for this long → lost
/**
 * A contact the check at Start found lifted reads No contact this long, whatever
 * the resistance says: the Pro's resistance can read a lifted contact as Good
 * (1.9.8, run 3), so it must not hand Ready straight back.
 */
export const LIFTED_HOLD_MS = 10_000

export type View = 'contacts' | 'signal'

export interface DeviceSummary {
  name: string
  serial: string | null
  family: string
  /** Contact quality is estimated from the signal, not measured. */
  estimated: boolean
}

/** What the in-page session builder reports back after `finish`. */
export interface Refusal {
  code: string
  message: string
}

export interface FinishReport {
  sessionId: string
  rows: number
  approxSeconds: number
  layoutId: string | null
  layoutLabel: string | null
  journalIncluded: boolean
  bytes: number
  refusals: readonly Refusal[]
}

/** `'ec'`: eyes closed alone, the default. `'eo_ec'`: eyes open, then eyes closed. */
export type Plan = 'ec' | 'eo_ec' | 'before_after'
export type Block = 1 | 2

/** Server-owned protocol and a monotonic anchor; the browser wall clock is never used. */
export interface ExperimentState {
  run: ExperimentRun
  plan: ExperimentPlan
  activity: Activity
  clock: { serverMs: number; receivedAt: number } | null
  role: CaptureRole | null
  headbandRemoved: boolean | null
  /** A lost local capture must be acknowledged by a newer server revision before retry. */
  lossRevision: number | null
  /** Retained attempts at the start of this local capture are not failures of that capture. */
  localAttemptAfter: number | null
}

export interface ExperimentView {
  stage: ExperimentRun['stage']
  role: CaptureRole | null
  serverNow: number | null
  activityRemainingS: number | null
  activityElapsedS: number | null
  settleRemainingS: number | null
  canStart: boolean
  canRetry: boolean
  headbandRemoved: boolean | null
}

function timestamp(value: string | null | undefined): number | null {
  if (value == null) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

const experimentTerminal = (run: ExperimentRun) => ['complete', 'incomplete', 'cancelled'].includes(run.stage)

function latestCaptureAttempt(run: ExperimentRun, role: CaptureRole | null) {
  return (run.attempts ?? []).filter((attempt) => attempt.capture_role === role)
    .reduce<NonNullable<ExperimentRun['attempts']>[number] | null>((latest, attempt) =>
      latest === null || attempt.attempt_index > latest.attempt_index ? attempt : latest, null)
}

function experimentAttemptRestart(s: State, now: number): State {
  const experiment = s.experiment
  if (!experiment) return s
  const latest = latestCaptureAttempt(experiment.run, experiment.role)
  return setPhase(freshFit(s, now), 'off', now, { reason: null, channels: [], device: null,
    experiment: { ...experiment, localAttemptAfter: latest?.attempt_index ?? 0 } })
}

/** Timers use server timestamps advanced only by the elapsed monotonic interval since receipt. */
export function selectExperiment(s: Pick<State, 'experiment'>, now: number): ExperimentView | null {
  const e = s.experiment
  if (!e) return null
  const serverNow = e.clock === null ? null : e.clock.serverMs + Math.max(0, now - e.clock.receivedAt)
  const remaining = (start: string | null | undefined, seconds: number | null): number | null => {
    const began = timestamp(start)
    return serverNow === null || began === null || seconds === null ? null : Math.max(0, seconds - (serverNow - began) / 1000)
  }
  const expected = e.run.stage === 'awaiting_before' ? 'before' : e.run.stage === 'awaiting_after' ? 'after' : null
  const eligible = e.run.capture_eligibility
  const activityStarted = timestamp(e.run.activity_started_at)
  const deadline = timestamp(eligible?.start_before)
  const inWindow = eligible?.start_before == null || (deadline !== null && serverNow !== null && serverNow <= deadline)
  const canStart = expected !== null && eligible?.capture_role === expected && eligible.can_start
    && serverNow !== null && inWindow && e.lossRevision === null && Number.isFinite(e.plan.reading_s) && e.plan.reading_s > 0
  return {
    stage: e.run.stage,
    role: e.role ?? expected,
    serverNow,
    activityRemainingS: e.run.activity_finished_at != null ? 0 : remaining(e.run.activity_started_at, e.activity.duration_s),
    activityElapsedS: serverNow === null || activityStarted === null ? null : Math.max(0, (serverNow - activityStarted) / 1000),
    settleRemainingS: e.run.settle_finished_at != null ? 0 : remaining(e.run.activity_finished_at, e.plan.settle_s),
    canStart,
    canRetry: canStart && eligible?.retry_allowed === true,
    headbandRemoved: e.headbandRemoved,
  }
}

function readingSeconds(s: State): number {
  return s.plan === 'before_after' && s.experiment ? s.experiment.plan.reading_s : RECORD_SECONDS
}

function betweenReadings(s: State): boolean {
  const e = s.experiment
  return e !== null && !e.run.after_recording_id && !(s.phase === 'recording' && e.role === 'after')
    && (Boolean(e.run.before_recording_id) || (e.role === 'before' && ['journal', 'processing', 'sending', 'scored'].includes(s.phase)))
}

/**
 * Block 1 of a paired session, from the moment it is sealed. `sealed` → the
 * controller is packaging it; `sending` → it passed its self-check and is on
 * its way while block 2 records; `sent` → the server accepted it (`jobId`);
 * `refused` → its self-check failed and the pair was abandoned (the machine is
 * in `lost` with reason `package_refused`); `send_failed` → the upload or the
 * submission failed for good, the pair will have no result, block 2 goes on as
 * the day's reading.
 */
export type BlockOneStatus = 'sealed' | 'sending' | 'sent' | 'refused' | 'send_failed'
export interface BlockOne {
  status: BlockOneStatus
  report: FinishReport | null
  jobId: string | null
  error: string | null
}

export type Effect =
  | { kind: 'device'; op: 'startSignal' | 'startResist' }
  | { kind: 'record'; op: 'start'; then: 'recording_started' }
  | { kind: 'record'; op: 'stop'; then?: 'recording_stopped' | 'block_sealed' }
  | { kind: 'session'; op: 'abort'; reason: 'stopped_early' | 'device_lost' | 'pair_abandoned' }
  | { kind: 'session'; op: 'finish'; then: 'finished'; fail: 'finish_failed' }
  | { kind: 'cue'; op: CueKind }

export type Event =
  | { type: 'connect' }
  | { type: 'unsupported' }
  | { type: 'connect_failed'; message?: string }
  | { type: 'connected'; channels: readonly string[]; device?: DeviceSummary | null; battery?: number | null }
  | { type: 'resist'; readings: Readonly<Record<string, number | null>>; at?: number; estimated?: boolean }
  | { type: 'samples'; at?: number }
  | { type: 'status'; connected?: boolean; battery?: number | null; at?: number }
  | { type: 'adjusted' }
  | { type: 'set_view'; view: View }
  | { type: 'set_plan'; plan: Plan }
  | { type: 'experiment_sync'; run: ExperimentRun; plan: ExperimentPlan; activity: Activity; receivedAt?: number }
  | { type: 'experiment_begin_capture' }
  | { type: 'experiment_removed' }
  | { type: 'experiment_retry' }
  /** `pairId` is the UUID v4 the controller minted for this attempt; used only when the plan is `eo_ec`. */
  | { type: 'start'; pairId?: string }
  | { type: 'recording_started'; at?: number }
  | { type: 'stop_early' }
  | { type: 'recording_stopped' }
  /** Block 1 of a pair is sealed (the `record stop` with this follow-up ran); block 2 may start. */
  | { type: 'block_sealed' }
  /** The controller packaged block 1: refused when `report.refusals` is non-empty. */
  | { type: 'block_one_built'; report: FinishReport }
  | { type: 'block_one_sent'; jobId: string }
  | { type: 'block_one_send_failed'; message?: string }
  /** From `lost` in block 1 of a pair: give the pair up and record eyes closed only. */
  | { type: 'record_ec_only' }
  | { type: 'journal_done'; answered: boolean }
  | { type: 'finished'; report: FinishReport }
  | { type: 'finish_failed'; message?: string }
  | { type: 'scored' }
  | { type: 'unscored' }
  | { type: 'new_attempt' }
  | { type: 'reset' }
  | { type: 'error'; message?: string }
  /** The headband's data can no longer be trusted (lost or malformed frames, a contact lost mid-signal). */
  | { type: 'fault'; message?: string }
  /** The contact check at Start found these contacts lifted: back to fitting, before anything is recorded. */
  | { type: 'contacts_lifted'; channels: readonly string[] }
  | { type: 'tick' }

export interface State {
  phase: Phase
  phaseAt: number
  reason: LostReason | null
  synthetic: boolean
  /** The paired session (1.9.13). `plan` survives attempts; `pairId` and `blockOne` are per attempt. */
  plan: Plan
  experiment: ExperimentState | null
  block: Block
  pairId: string | null
  blockOne: BlockOne | null
  view: View
  channels: string[]
  device: DeviceSummary | null
  battery: number | null
  readings: Record<string, number | null>
  lastReadingAt: number | null
  lastSamplesAt: number | null
  pinnedAt: number | null
  stableSince: number | null
  guidance: Guidance | null
  guidanceAt: number
  recordingStartedAt: number | null
  recordedSeconds: number
  stopRequested: boolean
  journalAnswered: boolean | null
  report: FinishReport | null
  finishError: string | null
  error: string | null
  /** Contacts the check at Start found lifted, and until when they read No contact (`LIFTED_HOLD_MS`). */
  liftedUntil?: Record<string, number>
}

export function initial(now = 0, opts: { synthetic?: boolean } = {}): State {
  return {
    phase: 'off',
    phaseAt: now,
    reason: null,
    synthetic: Boolean(opts.synthetic),
    plan: 'ec',
    experiment: null,
    block: 1,
    pairId: null,
    blockOne: null,
    view: 'contacts',
    channels: [],
    device: null,
    battery: null,
    readings: {},
    lastReadingAt: null,
    lastSamplesAt: null,
    pinnedAt: null,
    stableSince: null,
    guidance: null,
    guidanceAt: 0,
    recordingStartedAt: null,
    recordedSeconds: 0,
    stopRequested: false,
    journalAnswered: null,
    report: null,
    finishError: null,
    error: null,
  }
}

/** A fresh attempt keeps the person's `plan`; the pair id and block 1 belong to the attempt that ended. */
function freshFit(s: State, now: number): State {
  return {
    ...s,
    block: 1,
    pairId: null,
    blockOne: null,
    view: 'contacts',
    readings: {},
    lastReadingAt: null,
    lastSamplesAt: null,
    pinnedAt: null,
    stableSince: null,
    guidance: null,
    guidanceAt: 0,
    recordingStartedAt: null,
    recordedSeconds: 0,
    stopRequested: false,
    journalAnswered: null,
    report: null,
    finishError: null,
    error: null,
    phaseAt: now,
  }
}

function setPhase(s: State, phase: Phase, now: number, extra: Partial<State> = {}): State {
  return { ...s, phase, phaseAt: now, ...extra }
}

function contactsOf(s: State) {
  const lay = layout(s.channels)
  return lay.placed.map((p) => ({ channel: p.channel, loc: p.loc, kohm: s.readings[p.channel] }))
}

function tiersOf(s: State): Tier[] {
  return s.channels.map((ch) => tierOf(s.readings[ch]))
}

function readingIsFresh(s: State, now: number): boolean {
  return s.lastReadingAt != null && now - s.lastReadingAt < STALE_AFTER_MS
}

const inFitting = (ph: Phase) => ph === 'fitting' || ph === 'ready' || ph === 'stale'

/** Re-evaluate the gate, the hold, staleness and the guidance debounce. */
function evaluateFitting(s: State, now: number, phaseChanged: boolean): State {
  let next: State = { ...s }
  if (!inFitting(next.phase)) return next
  // Live-signal view: the headband streams signal, not resistance, so the
  // contact readings are pinned. They are shown with their age and neither
  // go stale nor advance the hold; the phase stays where it was.
  if (next.view === 'signal') return next

  // Staleness first: an old reading cannot hold the gate.
  if (!readingIsFresh(next, now)) {
    next.stableSince = null
    if (next.phase !== 'stale' && next.lastReadingAt != null) {
      next = setPhase(next, 'stale', now)
      phaseChanged = true
    } else if (next.phase !== 'stale' && next.lastReadingAt == null && now - next.phaseAt >= STALE_AFTER_MS) {
      // Connected but never received a reading.
      next = setPhase(next, 'stale', now)
      phaseChanged = true
    }
  } else {
    if (next.phase === 'stale') {
      next = setPhase(next, 'fitting', now)
      phaseChanged = true
    }
    const ok = okSet(tiersOf(next))
    if (ok) {
      if (next.stableSince == null) next.stableSince = now
      if (next.phase === 'fitting' && now - next.stableSince >= HOLD_MS) {
        next = setPhase(next, 'ready', now)
        phaseChanged = true
      }
    } else {
      next.stableSince = null
      if (next.phase === 'ready') {
        next = setPhase(next, 'fitting', now)
        phaseChanged = true
      }
    }
  }

  // Guidance: one next step, debounced.
  const g = guide({ phase: next.phase, contacts: contactsOf(next) })
  const cur = next.guidance
  const canChange =
    !cur || (g.key !== cur.key && (now - next.guidanceAt >= GUIDANCE_MIN_MS || g.key.indexOf('ready') === 0 || phaseChanged))
  if (canChange) {
    next.guidance = g
    next.guidanceAt = now
  }
  return next
}

export function fmtTimer(sec: number): string {
  const s = Math.max(0, Math.ceil(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

export interface Reduced {
  state: State
  effects: Effect[]
}

/**
 * Run one transition's effects ONE AFTER ANOTHER, each awaited, as
 * `calibrations/static/main.js` `runEffects` does. Start from the contacts view
 * is `[startSignal, record start]`: the recorder must not start before the
 * headband is back in signal mode, and on the classic that is a real switch
 * out of resistance mode (R5). `run` reports its own failures and must not throw.
 */
export async function runEffectsInOrder(effects: readonly Effect[], run: (effect: Effect) => Promise<void>): Promise<void> {
  for (const effect of effects) await run(effect)
}

/** The reducer. */
export function reduce(state: State, event: Event, now: number): Reduced {
  let s = state
  const effects: Effect[] = []

  switch (event.type) {
    case 'experiment_sync': {
      if (event.run.plan_id !== event.plan.id || event.run.activity_key !== event.activity.key
        || event.plan.activity_key !== event.activity.key || event.run.user_id !== event.plan.user_id) break
      const previous = s.experiment
      const sameRun = previous?.run.id === event.run.id
      if (sameRun && previous && event.run.revision < previous.run.revision) break
      const serverMs = timestamp(event.run.server_time)
      if (sameRun && previous && event.run.revision === previous.run.revision && serverMs !== null
        && previous.clock !== null && serverMs < previous.clock.serverMs) break
      const role = event.run.stage === 'awaiting_before' ? 'before' : event.run.stage === 'awaiting_after' ? 'after' : null
      const activeRole = sameRun && s.phase === 'recording' ? previous?.role : null
      const experiment: ExperimentState = {
        run: event.run,
        plan: event.plan,
        activity: event.activity,
        clock: serverMs === null ? null : { serverMs, receivedAt: event.receivedAt ?? now },
        role: activeRole ?? role ?? (sameRun ? previous?.role ?? null : null),
        headbandRemoved: event.run.refit === true || (sameRun && previous?.headbandRemoved === true) ? true
          : event.run.refit === false ? false : sameRun ? previous?.headbandRemoved ?? null
            : event.run.before_recording_id ? null : false,
        lossRevision: sameRun && previous?.lossRevision != null && event.run.revision <= previous.lossRevision
          ? previous.lossRevision : null,
        localAttemptAfter: sameRun && (role === null || role === previous?.role) ? previous?.localAttemptAfter ?? null : null,
      }
      const terminal = experimentTerminal(event.run)
      if ((!sameRun || terminal) && s.phase === 'recording') {
        effects.push({ kind: 'record', op: 'stop' }, { kind: 'session', op: 'abort', reason: 'stopped_early' })
      } else if (terminal && sameRun && previous && !experimentTerminal(previous.run)) {
        effects.push({ kind: 'session', op: 'abort', reason: 'stopped_early' })
      }
      // A refresh starts with connection/fit. A re-read at the same stage keeps
      // the local capture/journal/send work; the server is authoritative for stages.
      const keepLocal = sameRun && !terminal && (s.phase === 'recording' || previous?.run.stage === event.run.stage)
      const nextPhase = !terminal && event.run.stage === 'awaiting_after' && s.channels.length > 0 ? 'fitting' : 'off'
      s = keepLocal ? { ...s, plan: 'before_after', experiment }
        : setPhase(freshFit(s, now), nextPhase, now, { plan: 'before_after', experiment, reason: null })
      const latest = latestCaptureAttempt(event.run, role)
      const localCapture = sameRun && ['recording', 'journal', 'processing'].includes(state.phase)
      const failedHere = latest && ['lost', 'rejected', 'empty', 'failed'].includes(latest.status)
        && (experiment.localAttemptAfter === null || latest.attempt_index > experiment.localAttemptAfter)
      if (!terminal && role !== null && failedHere && !localCapture) {
        // This attempt is already retained by the server. Display it without
        // a second loss event, sound or abort; any retry starts a new buffer.
        s = setPhase(s, 'lost', now, { reason: 'lost', stopRequested: true })
      }
      break
    }
    case 'experiment_removed': {
      if (s.experiment && betweenReadings(s)) {
        s = { ...s, experiment: { ...s.experiment, headbandRemoved: true } }
      }
      break
    }
    case 'experiment_retry': {
      if (s.plan !== 'before_after') break
      const eligible = selectExperiment(s, now)
      const allowed = s.phase === 'kept' ? eligible?.canStart : s.phase === 'lost' && eligible?.canRetry
      if (!allowed) break
      s = experimentAttemptRestart(s, now)
      break
    }
    case 'connect': {
      if (s.experiment && (experimentTerminal(s.experiment.run) || s.phase === 'lost')) break
      if (!['off', 'lost', 'kept', 'scored', 'unscored', 'sending'].includes(s.phase)) break
      s = setPhase(freshFit(s, now), 'scanning', now, { reason: null })
      break
    }
    case 'unsupported': {
      s = setPhase(s, 'unsupported', now)
      break
    }
    case 'connect_failed': {
      s = setPhase(s, 'off', now, { error: event.message ?? 'Could not connect.' })
      break
    }
    case 'connected': {
      s = setPhase(freshFit(s, now), 'fitting', now, {
        channels: [...event.channels],
        device: event.device ?? null,
        battery: typeof event.battery === 'number' && Number.isFinite(event.battery) ? event.battery : s.battery,
      })
      s = evaluateFitting(s, now, true)
      break
    }
    case 'resist': {
      const readings = { ...s.readings }
      for (const ch of s.channels) {
        if ((s.liftedUntil?.[ch] ?? -Infinity) > now) continue
        if (Object.prototype.hasOwnProperty.call(event.readings, ch)) readings[ch] = event.readings[ch] ?? null
      }
      s = { ...s, readings, lastReadingAt: event.at ?? now }
      if (s.experiment && betweenReadings(s) && Object.values(event.readings).some((reading) => tierOf(reading) === 'none')) {
        s = { ...s, experiment: { ...s.experiment, headbandRemoved: true } }
      }
      // A headband can switch between measured and estimated readings (the
      // classic falls back to the estimate when its resistance readout fails,
      // R5): the screen names whichever the current readings are.
      if (typeof event.estimated === 'boolean' && s.device && s.device.estimated !== event.estimated) {
        s = { ...s, device: { ...s.device, estimated: event.estimated } }
      }
      s = evaluateFitting(s, now, false)
      break
    }
    case 'samples': {
      s = { ...s, lastSamplesAt: event.at ?? now }
      break
    }
    case 'status': {
      s = {
        ...s,
        battery: typeof event.battery === 'number' && Number.isFinite(event.battery) ? event.battery : s.battery,
      }
      if (event.connected === false && s.experiment && betweenReadings(s)) {
        s = { ...s, experiment: { ...s.experiment, headbandRemoved: true }, channels: [], device: null,
          readings: {}, lastReadingAt: null, stableSince: null }
      }
      if (s.phase === 'recording' && event.connected === false) return lost(s, now, 'lost', effects)
      if (inFitting(s.phase) && event.connected === false) {
        // The headband went away while fitting: back to Connect, honestly.
        s = setPhase(freshFit(s, now), 'off', now, { channels: [], device: null, error: 'The headband disconnected.' })
      }
      break
    }
    case 'adjusted': {
      if (inFitting(s.phase)) {
        s = { ...s, stableSince: null, guidance: null, guidanceAt: 0 }
        s = evaluateFitting(s, now, true)
      }
      break
    }
    case 'set_view': {
      if (!inFitting(s.phase)) break
      const view: View = event.view === 'signal' ? 'signal' : 'contacts'
      if (view === s.view) break
      if (view === 'signal') {
        // Starting the signal stream stops the resist stream on the device.
        s = { ...s, view, pinnedAt: s.lastReadingAt }
        effects.push({ kind: 'device', op: 'startSignal' })
      } else {
        // Back to contacts: a stale phase clears once a fresh reading lands.
        s = { ...s, view, stableSince: null, pinnedAt: null }
        effects.push({ kind: 'device', op: 'startResist' })
      }
      break
    }
    case 'set_plan': {
      // Chosen on Ready (spec §0: eyes closed alone stays the default). Allowed
      // throughout fitting so a choice made before the gate holds is kept.
      if (!inFitting(s.phase)) break
      // The guided plan can only be selected with a server-owned run attached.
      if (s.experiment && event.plan !== 'before_after') break
      const plan: Plan = event.plan === 'before_after' && s.experiment ? 'before_after' : event.plan === 'eo_ec' ? 'eo_ec' : 'ec'
      s = { ...s, plan }
      break
    }
    case 'experiment_begin_capture':
    case 'start': {
      if (s.phase !== 'ready') break
      if (event.type === 'experiment_begin_capture' && s.plan !== 'before_after') break
      const experiment = s.plan === 'before_after' ? selectExperiment(s, now) : null
      if (s.plan === 'before_after' && !experiment?.canStart) break
      if (s.plan === 'before_after' && (!okSet(tiersOf(s)) || !(s.view === 'signal' || readingIsFresh(s, now)))) break
      // A paired attempt without a pair id cannot link its two recordings: it
      // records eyes closed alone rather than sending two unlinked blocks.
      const paired = s.plan === 'eo_ec' && event.type === 'start' && typeof event.pairId === 'string' && event.pairId.length > 0
      s = setPhase(s, 'recording', now, {
        recordingStartedAt: null,
        recordedSeconds: 0,
        stopRequested: false,
        block: experiment?.role === 'after' ? 2 : 1,
        pairId: paired && event.type === 'start' ? event.pairId! : null,
        blockOne: null,
        plan: experiment ? 'before_after' : paired ? 'eo_ec' : 'ec',
        ...(s.experiment && experiment?.role ? { experiment: { ...s.experiment, role: experiment.role,
          localAttemptAfter: latestCaptureAttempt(s.experiment.run, experiment.role)?.attempt_index ?? 0 } } : {}),
      })
      if (s.view !== 'signal') effects.push({ kind: 'device', op: 'startSignal' })
      effects.push({ kind: 'record', op: 'start', then: 'recording_started' })
      break
    }
    case 'block_sealed': {
      // Block 1 is sealed; block 2 starts in the same effect run. The recording
      // phase stays; the clock restarts when the recorder confirms the start.
      if (s.phase !== 'recording' || s.plan !== 'eo_ec' || s.block !== 1) break
      s = {
        ...s,
        block: 2,
        recordingStartedAt: null,
        recordedSeconds: 0,
        stopRequested: false,
        blockOne: { status: 'sealed', report: null, jobId: null, error: null },
      }
      break
    }
    case 'block_one_built': {
      if (!s.blockOne) break
      if (event.report.refusals.length) {
        s = { ...s, blockOne: { ...s.blockOne, status: 'refused', report: event.report } }
        // The pair is abandoned: block 2 stops and nothing of it is sent. If the
        // machine has already left the recording (lost in block 2 meanwhile),
        // the status is all that changes.
        if (s.phase !== 'recording') break
        effects.push({ kind: 'record', op: 'stop' })
        return lost(s, now, 'package_refused', effects)
      }
      s = { ...s, blockOne: { ...s.blockOne, status: 'sending', report: event.report } }
      break
    }
    case 'block_one_sent': {
      if (!s.blockOne || s.blockOne.status !== 'sending') break
      s = { ...s, blockOne: { ...s.blockOne, status: 'sent', jobId: event.jobId } }
      break
    }
    case 'block_one_send_failed': {
      if (!s.blockOne || s.blockOne.status !== 'sending') break
      s = { ...s, blockOne: { ...s.blockOne, status: 'send_failed', error: event.message ?? 'The eyes-open recording could not be sent.' } }
      break
    }
    case 'record_ec_only': {
      // Only from a lost block 1 of a pair. The lost flow has already torn the
      // headband down (its abort), so the way back to Ready is through Connect,
      // as for a new attempt; the plan is what changes.
      if (s.phase !== 'lost' || !offersEcOnly(s)) break
      s = setPhase(freshFit(s, now), 'off', now, { reason: null, channels: [], device: null, plan: 'ec' })
      break
    }
    case 'recording_started': {
      if (s.phase !== 'recording') break
      s = { ...s, recordingStartedAt: event.at ?? now, lastSamplesAt: event.at ?? now }
      break
    }
    case 'stop_early': {
      if (s.phase !== 'recording') break
      effects.push({ kind: 'record', op: 'stop' })
      return lost(s, now, 'stopped', effects)
    }
    case 'recording_stopped': {
      if (s.phase !== 'recording') break
      const after = s.plan === 'before_after' && s.experiment?.role === 'after'
      s = setPhase(s, after ? 'processing' : 'journal', now, { recordedSeconds: readingSeconds(s) })
      if (after) effects.push({ kind: 'session', op: 'finish', then: 'finished', fail: 'finish_failed' })
      break
    }
    case 'journal_done': {
      if (s.phase !== 'journal') break
      s = setPhase(s, 'processing', now, { journalAnswered: event.answered })
      effects.push({ kind: 'session', op: 'finish', then: 'finished', fail: 'finish_failed' })
      break
    }
    case 'finished': {
      if (s.phase !== 'processing') break
      s = { ...s, report: event.report }
      s = event.report.refusals.length ? setPhase(s, 'kept', now) : setPhase(s, 'sending', now)
      break
    }
    case 'finish_failed': {
      if (s.phase !== 'processing') break
      s = setPhase(s, 'kept', now, { finishError: event.message ?? 'The recording could not be prepared.' })
      break
    }
    case 'scored': {
      if (s.phase !== 'sending') break
      s = setPhase(s, 'scored', now)
      break
    }
    case 'unscored': {
      if (s.phase !== 'sending') break
      s = setPhase(s, 'unscored', now)
      break
    }
    case 'new_attempt':
    case 'reset': {
      if (s.experiment && s.phase === 'lost') {
        if (event.type !== 'new_attempt' || !selectExperiment(s, now)?.canRetry) break
        s = experimentAttemptRestart(s, now)
        break
      }
      s = setPhase(freshFit(s, now), 'off', now, { reason: null, channels: [], device: null })
      break
    }
    case 'contacts_lifted': {
      // Only before the recording has started: nothing has been kept yet.
      if (s.phase !== 'recording' || s.recordingStartedAt != null || event.channels.length === 0) break
      const readings = { ...s.readings }
      const liftedUntil = { ...(s.liftedUntil ?? {}) }
      for (const ch of event.channels) {
        readings[ch] = Infinity
        liftedUntil[ch] = now + LIFTED_HOLD_MS
      }
      s = setPhase(s, 'fitting', now, {
        readings,
        liftedUntil,
        view: 'contacts',
        recordingStartedAt: null,
        recordedSeconds: 0,
        stopRequested: false,
        stableSince: null,
        pairId: null,
        lastReadingAt: now,
      })
      s = evaluateFitting(s, now, true)
      effects.push({ kind: 'device', op: 'startResist' })
      break
    }
    case 'fault': {
      // Refuse, never repair: a recording whose data broke is over.
      if (s.phase === 'recording' && !s.stopRequested) return lost({ ...s, error: event.message ?? null }, now, 'lost', effects)
      break
    }
    case 'error': {
      s = { ...s, error: event.message ?? 'Something went wrong.' }
      if (s.phase === 'scanning') s = setPhase(s, 'off', now)
      break
    }
    case 'tick': {
      if (inFitting(s.phase)) {
        s = evaluateFitting(s, now, false)
      } else if (s.phase === 'recording') {
        if (s.recordingStartedAt != null) {
          const rec = (now - s.recordingStartedAt) / 1000
          const length = readingSeconds(s)
          if (!s.stopRequested && rec >= length) {
            s = { ...s, recordedSeconds: length, stopRequested: true }
            if (s.plan === 'eo_ec' && s.block === 1) {
              // The block boundary, in one reduce and in this order (1.9.13 W1):
              // the switch cue (close your eyes), the stop that seals block 1,
              // the start of block 2. No phase in between, no pause, no button.
              effects.push({ kind: 'cue', op: 'switch' })
              effects.push({ kind: 'record', op: 'stop', then: 'block_sealed' })
              effects.push({ kind: 'record', op: 'start', then: 'recording_started' })
            } else {
              // The end cue first: the person's eyes are closed (D2).
              effects.push({ kind: 'cue', op: 'end' })
              effects.push({ kind: 'record', op: 'stop', then: 'recording_stopped' })
            }
          } else {
            s = { ...s, recordedSeconds: Math.min(length, rec) }
          }
          // Lost detection only once the recording has actually started.
          if (!s.stopRequested) {
            const noSamples = s.lastSamplesAt != null && now - s.lastSamplesAt > LOST_SAMPLES_MS
            if (noSamples) return lost(s, now, 'lost', effects)
          }
        }
      }
      break
    }
    default:
      break
  }
  return { state: s, effects }
}

function lost(s: State, now: number, reason: LostReason, effects: Effect[]): Reduced {
  const recorded = s.recordingStartedAt != null ? Math.min(readingSeconds(s), (now - s.recordingStartedAt) / 1000) : 0
  // A lost headband ends the recording too, and the person's eyes are closed:
  // the end cue, ahead of the abort (which awaits a Bluetooth teardown). Not on
  // Stop early (they pressed the button, eyes open), and not when the full
  // length already asked for the stop and played the cue (stopRequested). A
  // refused block 1 arrives seconds into block 2, eyes just closed: the cue too.
  if ((reason === 'lost' || reason === 'package_refused') && !s.stopRequested) effects.push({ kind: 'cue', op: 'end' })
  effects.push({
    kind: 'session',
    op: 'abort',
    reason: reason === 'stopped' ? 'stopped_early' : reason === 'package_refused' ? 'pair_abandoned' : 'device_lost',
  })
  return {
    state: setPhase(s, 'lost', now, { reason, recordedSeconds: recorded, stopRequested: true,
      ...(s.experiment ? { experiment: { ...s.experiment, lossRevision: s.experiment.run.revision } } : {}),
    }),
    effects,
  }
}

/**
 * Whether `lost` offers "Record eyes closed only" (spec §2 W1–W2): a pair that
 * ended in block 1, or whose block 1 was refused by its self-check. Lost in
 * block 2 is today's lost; block 1 is already sealed and sent on its own.
 */
export function offersEcOnly(s: State): boolean {
  return s.plan === 'eo_ec' && (s.block === 1 || s.reason === 'package_refused')
}

export interface ViewModel {
  phase: Phase
  reason: LostReason | null
  copy: ReturnType<typeof copyFor>
  badge: ReturnType<typeof badgeFor>
  steps: ReturnType<typeof stepsFor>
  layout: Layout
  rows: ReturnType<typeof mapRows>
  tiersByChannel: Record<string, Tier>
  kohmByChannel: Record<string, number | null | undefined>
  view: View
  inSignalView: boolean
  legend: string
  contactCount: number
  checkText: string
  checkStale: boolean
  age: number | null
  guidance: Guidance | null
  canStart: boolean
  canAdjust: boolean
  showSettle: boolean
  settlePct: number
  heldText: string
  timer: string
  recordedText: string
  progressPct: number
  estimated: boolean
  report: FinishReport | null
  finishError: string | null
  error: string | null
  synthetic: boolean
  device: DeviceSummary | null
  battery: number | null
  channels: string[]
  /** The paired session (1.9.13). With the flag off these read 'ec' · 1 · null · null · false. */
  plan: Plan
  block: Block
  pairId: string | null
  blockOne: BlockOne | null
  canRecordEcOnly: boolean
  experiment: ExperimentView | null
}

/** View model for the screen. Pure; recomputed on every render. */
export function select(s: State, now: number): ViewModel {
  const ph = s.phase
  const lay = layout(s.channels)
  const tiersByChannel: Record<string, Tier> = {}
  for (const ch of s.channels) tiersByChannel[ch] = tierOf(s.readings[ch])
  const tiers = tiersOf(s)
  const age = s.lastReadingAt == null ? null : Math.max(0, Math.floor((now - s.lastReadingAt) / 1000))
  const isFitting = inFitting(ph)
  const guidance = isFitting ? s.guidance ?? guide({ phase: ph, contacts: contactsOf(s) }) : null
  const length = readingSeconds(s)
  const remaining = length - s.recordedSeconds
  const timer = fmtTimer(ph === 'recording' ? remaining : ph === 'lost' ? s.recordedSeconds : length)
  const held = heldCount(tiers)
  const n = s.channels.length
  const settleMs = s.stableSince ? now - s.stableSince : 0
  const inSignalView = isFitting && s.view === 'signal'
  const checkText =
    age == null
      ? isFitting
        ? 'Waiting for the first reading'
        : ''
      : inSignalView
        ? `Contacts pinned ${age} s ago`
        : ph === 'stale'
          ? `Last reading ${age} s ago`
          : age === 0
            ? 'Checked just now'
            : `Checked ${age} s ago`
  const kohmByChannel: Record<string, number | null | undefined> = {}
  for (const ch of s.channels) kohmByChannel[ch] = s.readings[ch]

  return {
    phase: ph,
    reason: s.reason,
    copy: copyFor(ph, { reason: s.reason }),
    badge: badgeFor(ph, { battery: s.battery, age, timer, reason: s.reason, synthetic: s.synthetic }),
    steps: stepsFor(ph, { reason: s.reason }),
    layout: lay,
    rows: mapRows(lay, tiersByChannel),
    tiersByChannel,
    kohmByChannel,
    view: s.view,
    inSignalView,
    legend: legendText(lay),
    contactCount: n,
    checkText,
    checkStale: ph === 'stale',
    age,
    guidance,
    canStart: ph === 'ready' && okSet(tiers) && (inSignalView || readingIsFresh(s, now))
      && (s.plan !== 'before_after' || selectExperiment(s, now)?.canStart === true),
    canAdjust: isFitting && Boolean(guidance && guidance.adjust),
    showSettle: ph === 'fitting' && Boolean(guidance && guidance.key === 'settle'),
    settlePct: Math.min(100, Math.round((settleMs / HOLD_MS) * 100)),
    heldText: n ? (held === n ? `${n} of ${n}` : `${held} of ${n} · ${n - held} counted for less`) : '',
    timer,
    recordedText: fmtTimer(s.recordedSeconds),
    progressPct: Math.min(100, (s.recordedSeconds / length) * 100),
    estimated: Boolean(s.device?.estimated),
    report: s.report,
    finishError: s.finishError,
    error: s.error,
    synthetic: s.synthetic,
    device: s.device,
    battery: s.battery,
    channels: s.channels,
    plan: s.plan,
    block: s.block,
    pairId: s.pairId,
    blockOne: s.blockOne,
    canRecordEcOnly: ph === 'lost' && offersEcOnly(s),
    experiment: selectExperiment(s, now),
  }
}
